import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import myaxios from "@/lib/axios";
import { API_ENDPOINT } from "@/lib/constants";

type SSOConnection = { slug: string; display_name: string };

/**
 * Single sign-on, on its own page.
 *
 * Kept separate from the password form on purpose: the great majority of
 * RaidoHub customers sign in with a password, and they should never see this
 * machinery. It also means the main login page makes no request while the user
 * is typing, and no field appears or disappears underneath them.
 *
 * The address is used only to work out which provider to send the browser to.
 */
function SSOLogin() {
  const [email, setEmail] = useState("");
  const [connections, setConnections] = useState<SSOConnection[]>([]);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<string>();

  useEffect(() => {
    document.title = "RaidoHub | Single sign-on";
  }, []);

  const startSSO = (slug: string) => {
    // A full navigation, not a fetch: the browser has to leave this origin for
    // the identity provider.
    window.location.href = `${API_ENDPOINT}/api/auth/sso/start?connection=${encodeURIComponent(
      slug
    )}`;
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address.includes("@")) {
      setMessage("Enter your full work email address.");
      return;
    }

    setChecking(true);
    setMessage(undefined);
    setConnections([]);

    try {
      const response = await myaxios.get("/api/auth/sso/connections", {
        params: { email: address },
      });
      const found: SSOConnection[] = response.data ?? [];

      if (found.length === 0) {
        setMessage(
          "Single sign-on isn't set up for that email domain. Sign in with your password instead, or ask your administrator."
        );
        return;
      }

      // One provider is the normal case, so go straight there rather than
      // asking the user to confirm a choice they do not have.
      if (found.length === 1) {
        startSSO(found[0].slug);
        return;
      }
      setConnections(found);
    } catch {
      setMessage("We could not check that address. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 px-4 relative overflow-hidden">
      <div className="mb-8 text-center z-10">
        <h1 className="text-3xl font-bold text-white">
          Welcome to <span className="text-blue-400">RaidoHub</span>
        </h1>
      </div>

      <Card className="w-full max-w-md border-0 shadow-xl bg-gray-800/70 backdrop-blur-sm z-10">
        <form onSubmit={handleContinue}>
          <CardHeader>
            <CardTitle className="text-2xl">Single sign-on</CardTitle>
            <CardDescription>
              <div>Enter your work email to continue to your provider.</div>
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {message && (
              <div className="opacity-70 dark:bg-orange-900 border dark:border-orange-600 rounded px-4 py-2 mb-4 text-sm">
                {message}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="sso-email">Work email</Label>
              <Input
                id="sso-email"
                type="email"
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-gray-500 bg-gray-700/50 hover:border-gray-400
                focus:border-blue-400/30 focus:outline-none focus:ring-0
                transition-all duration-300 focus:bg-gray-700/80"
              />
            </div>

            {/* Only reached when one address maps to more than one provider. */}
            {connections.length > 1 && (
              <div className="space-y-2 pt-2">
                <p className="text-sm text-gray-300">Choose a provider:</p>
                {connections.map((connection) => (
                  <Button
                    key={connection.slug}
                    type="button"
                    variant="outline"
                    className="w-full border-gray-500 bg-gray-700/40 text-white hover:bg-gray-700/70"
                    onClick={() => startSSO(connection.slug)}
                  >
                    {connection.display_name}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-3">
            <Button
              type="submit"
              disabled={checking}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white"
            >
              {checking ? "Checking…" : "Continue"}
            </Button>
          </CardFooter>
        </form>
      </Card>

      <div className="mt-6 text-center text-gray-300 z-10">
        <Link
          to="/login"
          className="text-blue-400 hover:text-blue-300 hover:underline"
        >
          Back to password sign-in
        </Link>
      </div>
    </div>
  );
}

export default SSOLogin;
